import { pipe, tap, cond, pathEq, T } from 'ramda';
import bsapParamsGenerator from './bsap';
import genericParamsGenerator from './generic';
import { validateProps } from '../utils/props';

const requiredProps = ['caseInfo', 'accessToken', 'baseUrl', 'clientContext', 'bankId'];

const OK = 200;

/**
 * Returns a generator that can generate an object containing functions for
 * each of the supported API calls. Each function returns parameters (URI,
 * method, etc) that can be used to execute that specific API call.
 * @type {(clientProps: Object) => (clientProps: Object) => Object}
 */
const getCaseSpecificParamsGenerator = cond([
  [pathEq(['caseInfo', 'type'], 'bsap'), bsapParamsGenerator],
  [T, genericParamsGenerator]
]);

/**
 * Checks that the status code for a response is 200
 * @param {Object} response - response object
 * @returns {Object} response - provided response object
 * @throws {Error} error when status code is not 200
 */
const validateStatusCode = response => {
  if (response.status !== OK) {
    throw new Error(`Unexpected status code ${response.status}`);
  }
  return response;
};

/**
 * Converts a parameter-generating method into a method that can
 * execute a fetch request using those parameters.
 *
 * Takes a method that can generate the options required
 * to execute a fetch request when provided with some parameters.
 * Returns a method that takes those parameters, passes them
 * to the provided method, and uses the returned parameters
 * to execute a fetch request, returning the resulting promise.
 * @type {(paramGenerator: (clientProps: Object) => Object) => (...any[]) => Promise}
 */
const createRequestFunc = onError => paramGenerator => (...params) => {
  const { uri, ...options } = paramGenerator(...params);
  return fetch(uri, { ...options })
    .then(validateStatusCode)
    .then(response => response.json())
    .catch(err => {
      // report the error to a callback so that the caller can handle all
      // errors at a single point. We still need to throw the error to
      // prevent the rest of the promise chain from executing.
      onError(err);
      throw err;
    });
};

/**
 * Creates an API client from an object containing methods
 * that can generate parameters required to execute an API
 * request, using a provided to generator to perform the
 * conversion.
 * @type {(Function) => (Object) => (Object)}
 */
const createClientFromParams = generator => params =>
  Object.assign(
    ...Object.entries(params).map(([key, value]) => ({
      [key]: generator(value)
    }))
  );

/**
 * Creates headers required for the requests
 * @param {string} accessToken accessToken for the request
 * @param {*} clientContext client context for the request
 */
const getHeaders = (accessToken, clientContext) => ({
  Authorization: `Bearer ${accessToken}`,
  'x-bpi-client-context': clientContext,
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'x-bpi-service-context': 'USER',
  'x-bpi-version': '1.2.0'
});

/**
 * Takes an object containing information required to
 * create the API client and returns an object with
 * methods for carrying out API requests.
 * @type {(clientProps: { caseInfo, accessToken, baseUrl, clientContext, bankId }) => Object}
 */
const createClient = (clientProps, onError) =>
  pipe(
    tap(validateProps(requiredProps)),
    ({ accessToken, clientContext, ...clientParams }) => ({
      headers: getHeaders(accessToken, clientContext),
      ...clientParams
    }),
    getCaseSpecificParamsGenerator,
    createClientFromParams(createRequestFunc(onError))
  )(clientProps);

export { createClient };
